/**
 * @fileoverview Job File Management Hook
 * 
 * Provides comprehensive file management for jobs including upload, download,
 * deletion, and access control. Files are stored in Supabase Storage and
 * metadata tracked in the job_files table with visibility controls, versioning,
 * and audit logging.
 * 
 * File Visibility Levels:
 * - private: Only uploader can view (draft files)
 * - public: All job participants can view
 * - award_only: Only visible after award (vendor work products)
 * - client_visible: Client organization members only
 * 
 * File Categories:
 * - technical_drawing: CAD files, blueprints, specifications
 * - purchase_order: PO documents and amendments
 * - invoice: Invoices and billing documents
 * - delivery_proof: Packing slips, delivery receipts
 * - quality_report: Inspection reports, test results
 * - other: General documents
 * 
 * File Validation:
 * - Maximum size: 200MB per file
 * - Allowed types: PDF, images (JPG/PNG), DOCX, CSV, XLSX, ZIP, TXT
 * - Validation occurs before upload
 * 
 * Access Control:
 * - Files have visibility levels controlling who can access
 * - All downloads logged in file_access_logs for audit trail
 * - Signed URLs expire after 1 hour for security
 * 
 * Notification System:
 * - Clients notified when vendors upload files
 * - Vendors notified when clients upload files
 * - Notifications respect user preferences
 * - Email notifications sent for important uploads
 * 
 * Key Features:
 * - Multi-file upload support
 * - File validation and size limits
 * - Visibility control and permissions
 * - Download with signed URLs
 * - Access logging and audit trail
 * - Soft delete (files marked inactive)
 * - Automatic notifications
 * - File categorization
 * - Version tracking
 * 
 * @module useJobFiles
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { 
  TUS_THRESHOLD,
  SUPABASE_URL,
  createTusUpload, 
  createProgressUpload,
  UploadAbortedError,
  type UploadProgress,
  type UploadController 
} from '@/lib/upload';

/**
 * Job file metadata record
 * 
 * Tracks all metadata for files attached to jobs. Files are stored in Supabase
 * Storage with keys in the format: jobs/{jobId}/{userId}/{filename}
 */
export interface JobFile {
  id: string;
  job_id: string;
  organization_id: string;
  uploader_user_id: string;
  uploader_role: 'client' | 'vendor';
  visibility_level: 'private' | 'public' | 'award_only' | 'client_visible';
  filename: string;
  original_filename: string;
  file_size_bytes: number;
  content_type: string;
  storage_key: string;
  file_category: string;
  file_version: number;
  replaced_file_id?: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  public_id: string;
}

/**
 * Data required to upload a new file
 */
export interface FileUploadData {
  file: File;
  visibility_level: JobFile['visibility_level'];
  file_category: JobFile['file_category'];
  description?: string;
}

interface UseJobFilesOptions {
  jobId: string;
}

/** Maximum file size allowed (200MB) */
export const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB

/** Allowed MIME types for file uploads */
export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/zip',
  'text/plain'
];

/**
 * Hook for managing files attached to a job
 * 
 * Provides complete file lifecycle management including upload, download,
 * deletion, visibility control, and access logging. Files are automatically
 * categorized and notifications sent to relevant parties on upload.
 * 
 * Upload Flow:
 * 1. Validate file size and type
 * 2. Determine uploader role (client vs. vendor)
 * 3. Upload file to Supabase Storage
 * 4. Create metadata record in job_files table
 * 5. Send notification to counterparty
 * 6. Refresh file list
 * 
 * Access Control:
 * - Visibility level controls who can access each file
 * - award_only files hidden until job is awarded
 * - private files only visible to uploader
 * - All access logged for compliance and audit
 * 
 * Notifications:
 * - Client uploads trigger vendor notifications
 * - Vendor uploads trigger client organization notifications
 * - Notifications include file name and job title
 * - Email notifications sent based on user preferences
 * 
 * @param options - Hook options including job ID
 * @returns Object with file data, upload/download functions, and state
 * 
 * @example
 * ```tsx
 * function JobDocuments({ jobId }) {
 *   const {
 *     files,
 *     clientFiles,
 *     vendorFiles,
 *     uploading,
 *     uploadFile,
 *     getDownloadUrl,
 *     deleteFile
 *   } = useJobFiles({ jobId });
 * 
 *   const handleUpload = async (file: File) => {
 *     try {
 *       await uploadFile({
 *         file,
 *         visibility_level: 'public',
 *         file_category: 'technical_drawing',
 *         description: 'Part specifications'
 *       });
 *       toast({ title: 'File uploaded successfully' });
 *     } catch (error) {
 *       toast({ title: 'Upload failed', variant: 'destructive' });
 *     }
 *   };
 * 
 *   const handleDownload = async (file: JobFile) => {
 *     const url = await getDownloadUrl(file);
 *     window.open(url, '_blank');
 *   };
 * 
 *   return (
 *     <div>
 *       <FileUploader onUpload={handleUpload} disabled={uploading} />
 *       <FileList files={files} onDownload={handleDownload} />
 *     </div>
 *   );
 * }
 * ```
 */
export const useJobFiles = ({ jobId }: UseJobFilesOptions) => {
  const [files, setFiles] = useState<JobFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const uploadControllerRef = useRef<UploadController | null>(null);

  /**
   * Fetches all active files for the job
   * 
   * Queries job_files table for files matching the job ID with is_active=true.
   * Results ordered by creation date descending (newest first).
   */
  const fetchFiles = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('job_files')
        .select('*')
        .eq('job_id', jobId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setFiles((data as JobFile[]) || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching job files:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchFiles();
    }
  }, [jobId]);

  /**
   * Validates a file before upload
   * 
   * Checks file size and MIME type against allowed values.
   * 
   * @param file - File to validate
   * @returns Error message if invalid, null if valid
   * 
   * @example
   * ```typescript
   * const error = validateFile(selectedFile);
   * if (error) {
   *   alert(error);
   *   return;
   * }
   * // Proceed with upload
   * ```
   */
  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 200MB limit`;
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return `File type not allowed. Allowed types: PDF, JPG, PNG, DOCX, CSV, XLSX, ZIP, TXT`;
    }

    return null;
  };

  /**
   * Uploads a file to the job
   * 
   * Complete upload flow including validation, storage upload, metadata creation,
   * and notification sending. Automatically determines uploader role by checking
   * if user is the awarded vendor.
   * 
   * Upload Process:
   * 1. Validate file size and type
   * 2. Authenticate user and get profile
   * 3. Determine if uploader is client or vendor
   * 4. Generate unique storage key
   * 5. Upload file to Supabase Storage
   * 6. Create metadata record in job_files
   * 7. Send notifications to counterparty
   * 8. Refresh file list
   * 
   * Role Determination:
   * - Check if user is awarded vendor for this job
   * - If awarded vendor, role is 'vendor'
   * - Otherwise, role is 'client'
   * 
   * Notifications:
   * - Client uploads notify vendor
   * - Vendor uploads notify all client organization members
   * - Notifications respect user preference settings
   * - Includes job title, filename, and link to documents tab
   * 
   * @param uploadData - File and metadata for upload
   * @returns Created JobFile record
   * @throws Error if validation fails or upload fails
   * 
   * @example
   * ```typescript
   * const newFile = await uploadFile({
   *   file: selectedFile,
   *   visibility_level: 'award_only',
   *   file_category: 'quality_report',
   *   description: 'Final inspection report'
   * });
   * console.log('Uploaded file ID:', newFile.id);
   * ```
   */
  /**
   * Ensures a fresh access token for long uploads
   * Refreshes the session to prevent token expiry during large file uploads
   */
  const ensureFreshToken = async (): Promise<string> => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) {
      throw new Error('Failed to refresh authentication session');
    }
    return data.session.access_token;
  };

  const uploadFile = async (uploadData: FileUploadData) => {
    try {
      setUploading(true);
      setUploadProgress({ bytesUploaded: 0, bytesTotal: uploadData.file.size, percentage: 0 });
      
      // Validate file
      const validationError = validateFile(uploadData.file);
      if (validationError) {
        throw new Error(validationError);
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Refresh token to ensure it's valid for the entire upload duration
      const accessToken = await ensureFreshToken();

      // Get user profile to determine role and organization
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id, role')
        .eq('user_id', userData.user.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // Check if user is a vendor and determine their role
      let uploaderRole: 'client' | 'vendor' = 'client';
      
      // Check if user is an awarded vendor for this job
      const { data: award } = await supabase
        .from('awards')
        .select('winning_bid_id')
        .eq('job_id', jobId)
        .maybeSingle();

      if (award) {
        const { data: bid } = await supabase
          .from('bids')
          .select('vendor_user_id')
          .eq('id', award.winning_bid_id)
          .eq('vendor_user_id', userData.user.id)
          .maybeSingle();

        if (bid) {
          uploaderRole = 'vendor';
        }
      }

      // Generate unique filename
      const fileExt = uploadData.file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const storageKey = `jobs/${jobId}/${userData.user.id}/${fileName}`;

      // Decide upload method based on file size
      const useTus = uploadData.file.size >= TUS_THRESHOLD;

      // Upload to Supabase Storage with progress tracking
      await new Promise<void>((resolve, reject) => {
        const handleProgress = (progress: UploadProgress) => {
          setUploadProgress(progress);
        };

        const handleError = (err: Error) => {
          uploadControllerRef.current = null;
          reject(err);
        };

        const handleSuccess = () => {
          uploadControllerRef.current = null;
          setUploadProgress({ 
            bytesUploaded: uploadData.file.size, 
            bytesTotal: uploadData.file.size, 
            percentage: 100 
          });
          resolve();
        };

        const handleAbort = () => {
          uploadControllerRef.current = null;
          reject(new UploadAbortedError());
        };

        if (useTus) {
          // Use TUS for large files (resumable)
          const controller = createTusUpload({
            file: uploadData.file,
            bucketName: 'documents',
            objectPath: storageKey,
            accessToken,
            onProgress: handleProgress,
            onError: handleError,
            onSuccess: handleSuccess,
            onAbort: handleAbort,
          });
          uploadControllerRef.current = controller;
          controller.start();
        } else {
          // Use XHR for smaller files (simpler, with progress)
          const url = `${SUPABASE_URL}/storage/v1/object/documents/${storageKey}`;
          const controller = createProgressUpload({
            file: uploadData.file,
            url,
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'x-upsert': 'true',
            },
            onProgress: handleProgress,
            onError: handleError,
            onSuccess: handleSuccess,
            onAbort: handleAbort,
          });
          uploadControllerRef.current = controller;
          controller.start();
        }
      });

      // Create file record
      const { data: fileData, error: fileError } = await supabase
        .from('job_files')
        .insert({
          job_id: jobId,
          organization_id: profile.organization_id,
          uploader_user_id: userData.user.id,
          uploader_role: uploaderRole,
          visibility_level: uploadData.visibility_level,
          filename: fileName,
          original_filename: uploadData.file.name,
          file_size_bytes: uploadData.file.size,
          content_type: uploadData.file.type,
          storage_key: storageKey,
          file_category: uploadData.file_category,
          description: uploadData.description,
        })
        .select()
        .single();

      if (fileError) throw fileError;

      // Trigger notification via edge function (handles email access with service role)
      try {
        const { data: jobData } = await supabase
          .from('jobs')
          .select('public_id')
          .eq('id', jobId)
          .single();

        if (jobData) {
          await invokeEdgeFunction('file-upload-notify', {
            body: {
              fileId: fileData.id,
              jobId: jobId,
              filename: uploadData.file.name,
              fileCategory: uploadData.file_category || 'uncategorized',
              uploaderRole: uploaderRole,
              visibilityLevel: uploadData.visibility_level
            },
            callerOperation: 'useJobFiles.notifyUpload',
          });
        }
      } catch (notifyError) {
        console.error('Error sending file upload notification:', notifyError);
        // Don't fail the upload if notification fails
      }

      await fetchFiles();
      return fileData;
    } catch (err) {
      // Check if it was an intentional cancellation - clean exit without error
      if (err instanceof UploadAbortedError) {
        return null;
      }
      console.error('Error uploading file:', err);
      throw err;
    } finally {
      setUploading(false);
      setUploadProgress(null);
      uploadControllerRef.current = null;
    }
  };

  /**
   * Cancels the current upload in progress
   */
  const cancelUpload = () => {
    if (uploadControllerRef.current) {
      uploadControllerRef.current.abort();
      uploadControllerRef.current = null;
      setUploading(false);
      setUploadProgress(null);
    }
  };

  /**
   * Pauses the current TUS upload (only works for large files using TUS)
   */
  const pauseUpload = () => {
    if (uploadControllerRef.current?.pause) {
      uploadControllerRef.current.pause();
    }
  };

  /**
   * Resumes a paused TUS upload
   */
  const resumeUpload = () => {
    if (uploadControllerRef.current?.resume) {
      uploadControllerRef.current.resume();
    }
  };

  /**
   * Generates a temporary signed URL for downloading a file
   * 
   * Creates a signed URL with 1-hour expiration and logs the access for audit
   * purposes. Signed URLs provide secure, time-limited access to private files.
   * 
   * @param file - File to generate download URL for
   * @returns Signed URL string valid for 1 hour
   * @throws Error if URL generation fails
   * 
   * @example
   * ```typescript
   * const url = await getDownloadUrl(file);
   * // Open in new tab
   * window.open(url, '_blank');
   * 
   * // Or download directly
   * const link = document.createElement('a');
   * link.href = url;
   * link.download = file.original_filename;
   * link.click();
   * ```
   */
  const getDownloadUrl = async (file: JobFile) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(file.storage_key, 3600); // 1 hour expiration

      if (error) throw error;

      // Log file access for audit trail
      await logFileAccess(file.id, 'download');

      return data.signedUrl;
    } catch (err) {
      console.error('Error getting download URL:', err);
      throw err;
    }
  };

  /**
   * Logs file access for audit and compliance purposes
   * 
   * Records all file access events in file_access_logs table including
   * user ID, role, access type, and timestamp. Used for security audits
   * and compliance reporting.
   * 
   * Access Types:
   * - view: File previewed in browser
   * - download: File downloaded to device
   * - preview: Thumbnail or preview generated
   * 
   * @param fileId - ID of file being accessed
   * @param accessType - Type of access
   */
  const logFileAccess = async (fileId: string, accessType: 'view' | 'download' | 'preview') => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;

      // Get user role
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', userData.user.id)
        .single();

      let userRole = 'client';
      if (profile?.role === 'vendor') {
        userRole = 'vendor';
      } else if (profile?.role === 'admin') {
        userRole = 'admin';
      }

      await supabase
        .from('file_access_logs')
        .insert({
          file_id: fileId,
          accessed_by_user_id: userData.user.id,
          access_type: accessType,
          user_role: userRole,
        });
    } catch (err) {
      console.error('Error logging file access:', err);
      // Don't throw error for logging failures
    }
  };

  /**
   * Soft deletes a file
   * 
   * Marks file as inactive in database and removes from storage. Files are
   * soft deleted (is_active = false) to preserve audit trail while hiding
   * from normal queries.
   * 
   * @param fileId - ID of file to delete
   * @throws Error if deletion fails
   * 
   * @example
   * ```typescript
   * await deleteFile(file.id);
   * toast({ title: 'File deleted successfully' });
   * ```
   */
  const deleteFile = async (fileId: string) => {
    try {
      const file = files.find(f => f.id === fileId);
      if (!file) throw new Error('File not found');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([file.storage_key]);

      if (storageError) throw storageError;

      // Soft delete from database
      const { error: dbError } = await supabase
        .from('job_files')
        .update({ is_active: false })
        .eq('id', fileId);

      if (dbError) throw dbError;

      await fetchFiles();
    } catch (err) {
      console.error('Error deleting file:', err);
      throw err;
    }
  };

  /**
   * Updates file visibility level
   * 
   * Changes who can access the file by updating visibility_level.
   * Common use case: promoting award_only files to public after completion.
   * 
   * @param fileId - ID of file to update
   * @param visibility - New visibility level
   * @throws Error if update fails
   * 
   * @example
   * ```typescript
   * // Make file public after job completion
   * await updateFileVisibility(file.id, 'public');
   * ```
   */
  const updateFileVisibility = async (fileId: string, visibility: JobFile['visibility_level']) => {
    try {
      const { error } = await supabase
        .from('job_files')
        .update({ visibility_level: visibility })
        .eq('id', fileId);

      if (error) throw error;

      await fetchFiles();
    } catch (err) {
      console.error('Error updating file visibility:', err);
      throw err;
    }
  };

  // Group files by uploader role for easy filtering
  const clientFiles = files.filter(f => f.uploader_role === 'client');
  const vendorFiles = files.filter(f => f.uploader_role === 'vendor');

  return {
    files,
    clientFiles,
    vendorFiles,
    loading,
    error,
    uploading,
    uploadProgress,
    uploadFile,
    cancelUpload,
    pauseUpload,
    resumeUpload,
    getDownloadUrl,
    deleteFile,
    updateFileVisibility,
    logFileAccess,
    refetch: fetchFiles,
    validateFile
  };
};